/** Contenedores de layout. `AppViewport` es el "device" mobile-first; las
 *  pantallas de adulto (clínicas) usan `AdultScreen` + `AdultHeader`. El panel
 *  del terapeuta usa `TherapistLayout` (tab bar) + `ScreenHeader` contextual. */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.js";
import { useT } from "../i18n/index.js";
import styles from "./Layout.module.css";

export function AdultScreen({ children }: { children: ReactNode }) {
  return <section className={styles.adult}>{children}</section>;
}

export function AdultHeader({ title, right }: { title: string; right?: ReactNode }) {
  const { rol, logout } = useAuth();
  const t = useT();
  const roleLabel =
    rol === "padre" ? "MODO PADRE" : rol === "terapeuta" ? "MODO TERAPEUTA" : rol ? "ADMIN" : "";
  return (
    <header className={styles.appbar}>
      <div className={styles.logo}>V</div>
      <h1>{title}</h1>
      <div className={styles.headerRight}>
        {right}
        {rol ? (
          <>
            <span className={styles.role}>{roleLabel}</span>
            <button className={styles.logoutBtn} onClick={() => void logout()} title={t("common.logout")}>
              ⎋
            </button>
          </>
        ) : null}
      </div>
    </header>
  );
}

export function Content({ children }: { children: ReactNode }) {
  return <div className={styles.content}>{children}</div>;
}

/** Header contextual del panel del terapeuta. Con `back` muestra el chevron ‹ que
 *  vuelve a la ruta indicada (jerarquía explícita); sin `back` muestra el logo. El
 *  `right` por defecto es el botón de salir (paridad con AdultHeader). El tab bar
 *  del layout garantiza que nunca haya un callejón aunque falte back. */
export function ScreenHeader({
  title,
  back,
  right,
}: {
  title: string;
  back?: string;
  right?: ReactNode;
}) {
  const { logout } = useAuth();
  const t = useT();
  return (
    <header className={styles.screenHeader}>
      {back ? (
        <Link to={back} className={styles.backBtn} aria-label={t("nav.back")}>
          ‹
        </Link>
      ) : (
        <div className={styles.logo}>V</div>
      )}
      <h1 className={styles.screenTitle}>{title}</h1>
      <div className={styles.headerRight}>
        {right ?? (
          <button
            className={styles.logoutBtn}
            onClick={() => void logout()}
            title={t("common.logout")}
            aria-label={t("common.logout")}
          >
            ⎋
          </button>
        )}
      </div>
    </header>
  );
}
